import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * <cart-status> — "✓ 2 in your cart · View cart" under the product page's
 * buy buttons (blocks/custom-cart-status.liquid). A persistent confirmation
 * (Horizon's tick lasts 0.8 s), so shoppers don't click Add to cart again
 * just to be sure; it is also the clear answer to "did that work?".
 *
 * Source of truth is the cart Shopify returns:
 *   • first paint: quantities rendered by Liquid (data-items)
 *   • every add / change: the `cartLinesUpdate` event's promise resolves with
 *     the updated cart lines (Horizon's product form and cart drawer)
 *   • another variant chosen: Horizon's `productSelect` event; the selected
 *     variant id is read from the product form's own `id` input
 *
 * data-items  JSON { variantId: quantity } for this product
 * data-label  "[count] in your cart"
 * refs: text
 *
 * @extends {Component<{ text: HTMLElement }>}
 */
class CartStatus extends Component {
  requiredRefs = ['text'];

  /** @type {Record<string, number>} */
  #items = {};

  connectedCallback() {
    super.connectedCallback();
    try {
      this.#items = JSON.parse(this.dataset.items ?? '{}');
    } catch {
      this.#items = {};
    }
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onVariantChange);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onVariantChange);
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  /** The variant currently selected in this section's product form */
  get #variantId() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.#section?.querySelector('product-form-component form input[name="id"]') ?? null
    );
    return input?.value || this.dataset.variantId || '';
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then((result) => {
        const items = result?.detail?.items ?? result?.cart?.items;
        if (!Array.isArray(items)) return;
        /** @type {Record<string, number>} */
        const next = {};
        for (const item of items) {
          const id = String(item.variant_id ?? item.id ?? '').replace(/\D/g, '');
          if (id) next[id] = (next[id] ?? 0) + Number(item.quantity ?? 0);
        }
        this.#items = next;
        this.#render();
      })
      .catch(() => {});
  };

  /** @param {Event & { promise?: Promise<any> }} event */
  #onVariantChange = (event) => {
    // The form's id input is updated once Horizon has re-rendered the variant
    (event.promise ?? Promise.resolve()).finally(() => requestAnimationFrame(() => this.#render()));
  };

  #render() {
    const count = this.#items[this.#variantId] ?? 0;
    this.hidden = count === 0;
    this.refs.text.textContent = (this.dataset.label ?? '[count]').replace('[count]', String(count));
  }
}

if (!customElements.get('cart-status')) {
  customElements.define('cart-status', CartStatus);
}
